import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export type LightMode = "studio" | "flat";

export interface World {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  shelf: THREE.Group;
  /** Floor mesh. Exposed so focus mode can hide it alongside the shelf. */
  floor: THREE.Mesh;
  /** Container for cup groups. Picking is scoped to this. */
  cupRoot: THREE.Group;
  /** Register an update callback invoked once per frame. */
  onUpdate: (cb: (dt: number) => void) => void;
  /**
   * Switch between lighting setups. "studio" is the default — directional
   * key + fill + rim with soft shadows. "flat" disables the directionals
   * and shadows and dials up an ambient so every cup face is evenly lit;
   * useful for inspecting cups from any orbit angle.
   */
  setLightMode: (mode: LightMode) => void;
  /**
   * Set a background image (url) or clear it (null). 2:1 aspect ratio
   * images are interpreted as equirectangular 360° panoramas — the
   * background pans naturally as the camera orbits. Other aspect ratios
   * are treated as flat backdrops. Fades in/out via backgroundIntensity.
   */
  setBackground: (url: string | null) => void;
}

export function createWorld(canvas: HTMLCanvasElement): World {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#0b0d10");
  scene.backgroundIntensity = 1.0;
  scene.fog = new THREE.Fog("#0b0d10", 14, 40);

  const camera = new THREE.PerspectiveCamera(
    45,
    window.innerWidth / window.innerHeight,
    0.1,
    100,
  );
  camera.position.set(7, 4.5, 9);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.target.set(0, 1.4, 0);
  controls.minDistance = 3;
  controls.maxDistance = 20;
  controls.maxPolarAngle = Math.PI * 0.52;

  const lights = addLights(scene);
  const floor = buildFloor();
  scene.add(floor);
  const shelf = buildShelf();
  scene.add(shelf);

  const cupRoot = new THREE.Group();
  cupRoot.name = "cupRoot";
  scene.add(cupRoot);

  const defaultBackground = scene.background as THREE.Color;
  const bgState = {
    currentUrl: null as string | null,
    loaded: null as THREE.Texture | null,
  };
  const bgLoader = new THREE.TextureLoader();
  bgLoader.setCrossOrigin("anonymous");

  // Resize handling
  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  });

  // Animation loop
  const updaters: Array<(dt: number) => void> = [];
  const clock = new THREE.Clock();
  renderer.setAnimationLoop(() => {
    const dt = clock.getDelta();
    for (const u of updaters) u(dt);
    controls.update();
    renderer.render(scene, camera);
  });

  return {
    scene,
    camera,
    renderer,
    controls,
    shelf,
    floor,
    cupRoot,
    onUpdate: (cb) => {
      updaters.push(cb);
    },
    setLightMode: (mode) => applyLightMode(renderer, lights, mode),
    setBackground: (url) => {
      if (!url) {
        if (bgState.loaded) {
          scene.background = defaultBackground;
          scene.environment = null;
          bgState.loaded.dispose();
          bgState.loaded = null;
        }
        bgState.currentUrl = null;
        return;
      }
      if (url === bgState.currentUrl) return;
      bgState.currentUrl = url;
      bgLoader.load(
        url,
        (tex) => {
          if (bgState.currentUrl !== url) {
            tex.dispose();
            return;
          }
          tex.colorSpace = THREE.SRGBColorSpace;
          // 2:1 aspect ratio (within tolerance) → equirectangular
          // panorama, projected onto a sphere around the camera so it
          // pans as you orbit, and re-used as the scene environment so
          // the cups and shelf pick up image-based lighting from the
          // location. Other aspect ratios are flat backdrops only.
          const aspect = tex.image.width / tex.image.height;
          const isPanorama = aspect > 1.85 && aspect < 2.15;
          tex.mapping = isPanorama
            ? THREE.EquirectangularReflectionMapping
            : THREE.UVMapping;
          const old = bgState.loaded;
          scene.background = tex;
          scene.environment = isPanorama ? tex : null;
          bgState.loaded = tex;
          old?.dispose();
        },
        undefined,
        (err) => {
          console.warn(`[cupilot] background load failed for ${url}`, err);
        },
      );
    },
  };
}

interface SceneLights {
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  ambient: THREE.AmbientLight;
}

function applyLightMode(
  renderer: THREE.WebGLRenderer,
  lights: SceneLights,
  mode: LightMode,
): void {
  const studio = mode === "studio";
  // Studio: directional sun + sky/ground hemi + IBL from any loaded
  // panorama drive the look; soft shadows on. Flat: ambient does all the
  // work; directionals off; no shadows so cup backs aren't blacked out.
  lights.key.visible = studio;
  lights.fill.visible = studio;
  lights.hemi.intensity = studio ? 0.7 : 0.25;
  lights.ambient.intensity = studio ? 0.0 : 1.2;
  renderer.shadowMap.enabled = studio;
  renderer.toneMappingExposure = studio ? 1.0 : 1.05;
}

function addLights(scene: THREE.Scene): SceneLights {
  // Sky/ground hemisphere — broad ambient term tinted blue-from-above and
  // earth-from-below. Outdoor lighting basically.
  const hemi = new THREE.HemisphereLight(0xb1c5ff, 0x4a3a2a, 0.7);
  scene.add(hemi);

  // The "sun" — single warm directional that casts the contact shadow.
  // Frustum bounds need to cover the cat's roam zone (X ±22.5) and the
  // floor that extends ~15 units forward of the shelf — anything outside
  // the bounds silently drops from the shadow map (cups + cat near the
  // edges stop casting shadows). Bumped from ±10 to ±25 (horizontal) and
  // ±15 (vertical) with map size 4096 to keep shadows crisp over the
  // larger area (4096/50 ≈ 82 texels/unit vs the previous 2048/20 = 102
  // — about 20% softer than before).
  const key = new THREE.DirectionalLight(0xfff4e5, 2.0);
  key.position.set(6, 9, 6);
  key.castShadow = true;
  key.shadow.mapSize.set(4096, 4096);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 40;
  key.shadow.camera.left = -25;
  key.shadow.camera.right = 25;
  key.shadow.camera.top = 15;
  key.shadow.camera.bottom = -15;
  key.shadow.bias = -0.0005;
  scene.add(key);

  // Cool fill from the opposite side, simulating sky bounce — quieter
  // since image-based lighting from the panorama also fills shadow areas.
  const fill = new THREE.DirectionalLight(0xa6c8ff, 0.25);
  fill.position.set(-6, 4, -3);
  scene.add(fill);

  // Off by default; the light-mode toggle ramps this up to flatten the
  // scene so every cup face is evenly lit regardless of orbit angle.
  const ambient = new THREE.AmbientLight(0xffffff, 0.0);
  scene.add(ambient);

  return { hemi, key, fill, ambient };
}

/**
 * Invisible floor that only renders shadows — kept separate from the
 * shelf so focus mode can hide the shelf while still grounding the
 * focused cup with a contact shadow underneath.
 */
function buildFloor(): THREE.Mesh {
  const geom = new THREE.PlaneGeometry(60, 60);
  // White oak engineered hardwood. Source: Home Depot product photo
  // (Torrey Malibu wide-plank). Texture loads lazily — a quick
  // dark-brown placeholder is shown while it streams.
  const mat = new THREE.MeshStandardMaterial({
    color: 0x8a6a44,
    roughness: 0.7,
    metalness: 0.04,
  });
  const loader = new THREE.TextureLoader();
  loader.load("/textures/floor-white-oak.jpg", (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace;
    // MirroredRepeatWrapping flips every other tile, so the seam at a
    // tile boundary is the same pixel column on both sides — invisible.
    // Without this the source image's left/right edges don't line up
    // and you can pick out the repeats from across the room.
    tex.wrapS = THREE.MirroredRepeatWrapping;
    tex.wrapT = THREE.MirroredRepeatWrapping;
    // Source shows ~10 narrow planks per tile. Tile 5× over the
    // 60-unit floor → ~12-unit repeat → ~1.2-unit plank width.
    tex.repeat.set(5, 5);
    tex.anisotropy = 8;
    mat.map = tex;
    // Once the photo is in, the placeholder colour can fall back to
    // white so the texture's hues come through unmuted.
    mat.color.set(0xffffff);
    mat.needsUpdate = true;
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = "floor";
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * A simple wood-toned plinth with two shelf rows. Cups will be placed on
 * the rows; the geometry of the rows determines layout in main.ts.
 */
function buildShelf(): THREE.Group {
  const group = new THREE.Group();
  group.name = "shelf";

  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x2b211a,
    roughness: 0.7,
    metalness: 0.05,
  });

  const shelfWidth = 12;
  const shelfDepth = 1.6;
  const shelfThickness = 0.12;
  const rowYs = [0.9, 2.5];

  for (const y of rowYs) {
    const board = new THREE.Mesh(
      new THREE.BoxGeometry(shelfWidth, shelfThickness, shelfDepth),
      woodMat,
    );
    board.position.set(0, y, 0);
    board.castShadow = true;
    board.receiveShadow = true;
    group.add(board);
  }

  // Side supports
  const supportGeom = new THREE.BoxGeometry(0.18, 3.4, shelfDepth);
  for (const x of [-shelfWidth / 2 + 0.1, shelfWidth / 2 - 0.1]) {
    const support = new THREE.Mesh(supportGeom, woodMat);
    support.position.set(x, 1.7, 0);
    support.castShadow = true;
    support.receiveShadow = true;
    group.add(support);
  }

  group.userData.rowYs = rowYs;
  group.userData.rowWidth = shelfWidth;
  return group;
}
